/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Article Tile PD component — single editorial card for the Blog page grid
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';
import {
    FULL_IMAGE_CLASSES,
    getAlignClasses,
    getMarginBottomClass,
    getTextStyle,
    isFullImage,
} from '@/lib/page-designer/style-attributes';

/* v8 ignore start */
@Component('articleTile', {
    name: 'Article Tile',
    description: 'Single editorial card for blog/article grids. Image + eyebrow + title + body copy + CTA.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ArticleTileMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'body', name: 'Body Copy', type: 'text' })
    body?: string;

    @AttributeDefinition({ id: 'ctaText', name: 'CTA Text', type: 'string' })
    ctaText?: string;

    @AttributeDefinition({ id: 'ctaLink', name: 'CTA Link', type: 'url' })
    ctaLink?: string;

    @AttributeDefinition({
        id: 'headingFontStyle',
        name: 'Style – Heading Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    headingFontStyle?: string;

    @AttributeDefinition({
        id: 'headingFontSize',
        name: 'Style – Heading Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    headingFontSize?: string;

    @AttributeDefinition({
        id: 'headingColor',
        name: 'Style – Heading Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    headingColor?: string;

    @AttributeDefinition({
        id: 'bodyFontStyle',
        name: 'Style – Body Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    bodyFontStyle?: string;

    @AttributeDefinition({
        id: 'bodyFontSize',
        name: 'Style – Body Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    bodyFontSize?: string;

    @AttributeDefinition({
        id: 'bodyColor',
        name: 'Style – Body Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    bodyColor?: string;

    @AttributeDefinition({
        id: 'textAlign',
        name: 'Style – Text Alignment',
        type: 'enum',
        values: ['Default', 'Left', 'Center', 'Right'],
        defaultValue: 'Default',
    })
    textAlign?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;

    @AttributeDefinition({
        id: 'imageDisplay',
        name: 'Style – Image Display',
        type: 'enum',
        values: ['Crop to fit', 'Show full image'],
        defaultValue: 'Crop to fit',
        description: 'Show full image keeps the whole image at its natural height (no top/bottom crop).',
    })
    imageDisplay?: string;
}
/* v8 ignore stop */

export default function ArticleTile({
    image,
    imageAlt,
    eyebrow,
    title,
    body,
    ctaText,
    ctaLink,
    headingFontStyle,
    headingFontSize,
    headingColor,
    bodyFontStyle,
    bodyFontSize,
    bodyColor,
    textAlign,
    marginBottom,
    imageDisplay,
}: {
    image?: Image;
    imageAlt?: string;
    eyebrow?: string;
    title?: string;
    body?: string;
    ctaText?: string;
    ctaLink?: string;
    headingFontStyle?: string;
    headingFontSize?: string;
    headingColor?: string;
    bodyFontStyle?: string;
    bodyFontSize?: string;
    bodyColor?: string;
    textAlign?: string;
    marginBottom?: string;
    imageDisplay?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-60px' });
    const heading = getTextStyle(
        { fontStyle: headingFontStyle, fontSize: headingFontSize, color: headingColor },
        'heading'
    );
    const bodyText = getTextStyle({ fontStyle: bodyFontStyle, fontSize: bodyFontSize, color: bodyColor }, 'body');
    const align = getAlignClasses(textAlign);
    const fullImage = isFullImage(imageDisplay);

    return (
        <article
            ref={ref}
            className={cn('flex flex-col df-reveal', inView && 'df-reveal--show', getMarginBottomClass(marginBottom))}>
            {image?.url && (
                <div className={cn('relative overflow-hidden rounded-ui bg-muted mb-6', !fullImage && 'aspect-square')}>
                    <DynamicImage
                        src={image.url}
                        alt={imageAlt ?? ''}
                        widths={{ base: 768, lg: 720 }}
                        className={fullImage ? 'block w-full' : 'absolute inset-0 w-full h-full'}
                        imageProps={{
                            className: cn(
                                'transition-transform duration-700 hover:scale-105',
                                fullImage ? FULL_IMAGE_CLASSES : 'w-full h-full object-cover'
                            ),
                        }}
                    />
                    {/* RGBARRY-CUSTOMIZATION: stretched link makes the image clickable to the same
                        destination as the tile's CTA, which remains independently clickable below */}
                    {ctaText && ctaLink && <StretchedLink to={ctaLink} />}
                </div>
            )}
            <div className={cn('flex flex-col gap-4 px-1', align?.text, align?.items)}>
                {eyebrow && <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{eyebrow}</p>}
                <h3
                    style={{ fontFamily: 'var(--font-display)', ...heading.style }}
                    className={cn(
                        'text-2xl lg:text-3xl font-light leading-snug text-foreground min-h-[2lh]',
                        heading.className
                    )}>
                    {title ?? ''}
                </h3>
                {body && (
                    <p
                        style={bodyText.style}
                        className={cn('text-sm text-muted-foreground leading-relaxed', bodyText.className)}>
                        {body}
                    </p>
                )}
                {ctaText && ctaLink && (
                    <Button
                        asChild
                        className={cn(
                            'df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2',
                            align ? 'self-auto' : 'self-start'
                        )}>
                        <Link to={ctaLink}>{ctaText}</Link>
                    </Button>
                )}
            </div>
        </article>
    );
}
